import { Controller } from '@hotwired/stimulus'
import {
  setupShelfMarquee,
  pauseShelfMarquee,
  resumeShelfMarquee,
  prefersReducedMotion
} from '../utils/shelf_marquee'

export default class extends Controller {
  static targets = ['track', 'item', 'previous', 'next']
  static values = {
    autoplay: { type: Boolean, default: false },
    marquee: { type: Boolean, default: false },
    marqueeDuration: { type: Number, default: 30 },
    interval: { type: Number, default: 2000 },
    loopCount: Number
  }

  connect () {
    if (this.marqueeValue) {
      this.connectMarquee()
      return
    }

    this.index = 0
    this.cloneLoopItems()
    this.enableTransition()
    this.scrollToIndex(0, false)
    this.updateControls = this.updateControls.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.pauseAutoplay = this.stop.bind(this)
    this.resumeAutoplay = this.start.bind(this)
    this.resumeAfterInteraction = this.scheduleResume.bind(this)
    this.trackTarget.addEventListener('scroll', this.updateControls, { passive: true })
    window.addEventListener('resize', this.handleResize)

    if (this.autoplayValue && this.reducedMotion) {
      this.itemTargets.slice(this.loopCountValue).forEach((item) => item.classList.add('hidden'))
    }

    if (this.canAutoplay) {
      this.element.addEventListener('mouseenter', this.pauseAutoplay)
      this.element.addEventListener('mouseleave', this.resumeAutoplay)
      this.trackTarget.addEventListener('touchstart', this.pauseAutoplay, { passive: true })
      this.trackTarget.addEventListener('touchend', this.resumeAfterInteraction, { passive: true })
      this.start()
    }

    this.updateControls()
  }

  connectMarquee () {
    if (!this.hasTrackTarget) return

    this.pauseMarquee = () => pauseShelfMarquee(this.trackTarget)
    this.resumeMarquee = () => resumeShelfMarquee(this.trackTarget)

    setupShelfMarquee(this.trackTarget, {
      durationSeconds: this.marqueeDurationValue,
      reducedMotion: this.reducedMotion
    })

    this.element.addEventListener('mouseenter', this.pauseMarquee)
    this.element.addEventListener('mouseleave', this.resumeMarquee)
    this.trackTarget.addEventListener('touchstart', this.pauseMarquee, { passive: true })
    this.trackTarget.addEventListener('touchend', this.resumeMarquee, { passive: true })
  }

  cloneLoopItems () {
    if (!this.autoplayValue || this.reducedMotion || !this.hasLoopCountValue) return
    if (this.itemTargets.length > this.loopCountValue) return

    this.itemTargets.slice(0, this.loopCountValue).forEach((item) => {
      const clone = item.cloneNode(true)
      clone.setAttribute('aria-hidden', 'true')
      this.trackTarget.appendChild(clone)
    })
  }

  disconnect () {
    if (this.marqueeValue) {
      this.element.removeEventListener('mouseenter', this.pauseMarquee)
      this.element.removeEventListener('mouseleave', this.resumeMarquee)
      this.trackTarget?.removeEventListener('touchstart', this.pauseMarquee)
      this.trackTarget?.removeEventListener('touchend', this.resumeMarquee)
      return
    }

    this.stop()
    this.clearResetTimer()
    this.clearResumeTimer()
    this.trackTarget.removeEventListener('scroll', this.updateControls)
    window.removeEventListener('resize', this.handleResize)
    this.element.removeEventListener('mouseenter', this.pauseAutoplay)
    this.element.removeEventListener('mouseleave', this.resumeAutoplay)
    this.trackTarget.removeEventListener('touchstart', this.pauseAutoplay)
    this.trackTarget.removeEventListener('touchend', this.resumeAfterInteraction)
  }

  previous (event) {
    event?.preventDefault()

    if (this.canAutoplay) {
      this.stop()
      this.clearResetTimer()

      if (this.index <= 0) {
        this.index = this.loopCountValue
        this.scrollToIndex(this.index, false)
        this.trackTarget.offsetHeight
      }

      this.index -= 1
      this.scrollToIndex(this.index)
      this.scheduleResume()
      return
    }

    this.trackTarget.scrollBy({ left: -this.pageWidth, behavior: this.scrollBehavior })
  }

  next (event) {
    event?.preventDefault()

    if (this.canAutoplay) {
      this.stop()
      this.advance()
      this.scheduleResume()
      return
    }

    this.trackTarget.scrollBy({ left: this.pageWidth, behavior: this.scrollBehavior })
  }

  updateControls () {
    if (!this.hasTrackTarget) return

    if (this.canAutoplay) {
      this.toggleDisabled(this.previousTarget, false)
      this.toggleDisabled(this.nextTarget, false)
      return
    }

    const maxScroll = this.trackTarget.scrollWidth - this.trackTarget.clientWidth
    this.toggleDisabled(this.previousTarget, this.trackTarget.scrollLeft <= 2)
    this.toggleDisabled(this.nextTarget, this.trackTarget.scrollLeft >= maxScroll - 2)
  }

  advance () {
    this.index += 1
    this.scrollToIndex(this.index)

    if (this.index >= this.loopCountValue) {
      this.clearResetTimer()
      this.resetTimer = window.setTimeout(() => {
        this.index = 0
        this.scrollToIndex(0, false)
        this.trackTarget.offsetHeight
        this.enableTransition()
      }, 720)
    }
  }

  scrollToIndex (index, transition = true) {
    const item = this.itemTargets[index]
    const firstItem = this.itemTargets[0]
    if (!item || !firstItem) return

    if (transition) {
      this.enableTransition()
    } else {
      this.disableTransition()
    }

    this.trackTarget.style.transform = `translateX(-${item.offsetLeft - firstItem.offsetLeft}px)`
  }

  enableTransition () {
    if (!this.hasTrackTarget) return

    this.trackTarget.style.transition = 'transform 650ms ease'
  }

  disableTransition () {
    if (!this.hasTrackTarget) return

    this.trackTarget.style.transition = 'none'
  }

  handleResize () {
    if (this.canAutoplay) this.scrollToIndex(this.index, false)
    this.updateControls()
  }

  start () {
    if (!this.canAutoplay) return

    this.stop()
    this.timer = window.setInterval(() => this.advance(), this.intervalValue)
  }

  stop () {
    if (!this.timer) return

    window.clearInterval(this.timer)
    this.timer = null
  }

  scheduleResume () {
    if (!this.canAutoplay) return

    this.clearResumeTimer()
    this.resumeTimer = window.setTimeout(() => this.start(), this.intervalValue)
  }

  clearResumeTimer () {
    if (!this.resumeTimer) return

    window.clearTimeout(this.resumeTimer)
    this.resumeTimer = null
  }

  clearResetTimer () {
    if (!this.resetTimer) return

    window.clearTimeout(this.resetTimer)
    this.resetTimer = null
  }

  get pageWidth () {
    return Math.max(this.trackTarget.clientWidth * 0.85, 220)
  }

  get scrollBehavior () {
    return this.reducedMotion ? 'auto' : 'smooth'
  }

  get reducedMotion () {
    return prefersReducedMotion()
  }

  get canAutoplay () {
    return this.autoplayValue &&
      this.hasLoopCountValue &&
      this.loopCountValue > 1 &&
      !this.reducedMotion
  }

  toggleDisabled (button, disabled) {
    button.disabled = disabled
    button.classList.toggle('opacity-35', disabled)
    button.classList.toggle('cursor-not-allowed', disabled)
  }
}
